import { SaSmsGuard } from '@components/sa-sms-messaging/sa-sms-guard';
import { SaSmsNav } from '@components/sa-sms-messaging/sa-sms-nav';
import { SmsTenantTable } from '@components/sa-sms-messaging/sms-tenant-table';
import { ISaSmsTenantList, SaSmsFeatures } from '@interfaces/sa-sms';
import { useNavigation } from '@refinedev/core';
import { Alert, Button, Checkbox, Input, Select, Space } from 'antd';
import { useSaSmsErrorText, useSaSmsGet } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Filters = {
    search: string;
    tier?: string;
    flagged: boolean;
    paused: boolean;
    lapsed: boolean;
    a2pPending: boolean;
};
const FLAGS: Array<'flagged' | 'paused' | 'lapsed' | 'a2pPending'> = [
    'flagged',
    'paused',
    'lapsed',
    'a2pPending',
];

/** Super-admin SMS console: tenants list (marketing-sms plan P25, doc section 15). */
export const SaSmsMessagingIndex: React.FC = () => {
    const { t } = useTranslation(['sa-sms']);
    const { push } = useNavigation();
    const errorText = useSaSmsErrorText();
    const [filters, setFilters] = useState<Filters>({
        search: '',
        flagged: false,
        paused: false,
        lapsed: false,
        a2pPending: false,
    });
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    const { data, isLoading, isError, error } = useSaSmsGet<ISaSmsTenantList>('/tenants', {
        search: filters.search,
        tier: filters.tier,
        flagged: filters.flagged || undefined,
        paused: filters.paused || undefined,
        lapsed: filters.lapsed || undefined,
        a2pPending: filters.a2pPending || undefined,
        page,
    });
    const { data: features } = useSaSmsGet<SaSmsFeatures>('/features');

    const apply = (patch: Partial<Filters>) => {
        setFilters((f) => ({ ...f, ...patch }));
        setPage(1);
    };

    return (
        <SaSmsGuard>
            {() => (
                <section className="sa-sms-page">
                    <SaSmsNav active="tenants" />
                    <Space wrap style={{ marginBottom: 16 }}>
                        <Input.Search
                            allowClear
                            value={search}
                            placeholder={t('list.search')}
                            onChange={(e) => setSearch(e.target.value)}
                            onSearch={(value) => apply({ search: value.trim() })}
                            style={{ width: 260 }}
                        />
                        <Select
                            allowClear
                            placeholder={t('list.tier')}
                            style={{ width: 150 }}
                            value={filters.tier}
                            onChange={(tier) => apply({ tier })}
                            options={['new', 'standard', 'enterprise'].map((v) => ({
                                value: v,
                                label: t(`tier.${v}`),
                            }))}
                        />
                        {FLAGS.map((key) => (
                            <Checkbox
                                key={key}
                                checked={filters[key]}
                                onChange={(e) => apply({ [key]: e.target.checked })}
                            >
                                {t(`list.filter.${key}`)}
                            </Checkbox>
                        ))}
                    </Space>
                    {isError && (
                        <Alert
                            type="error"
                            showIcon
                            message={errorText(error)}
                            style={{ marginBottom: 16 }}
                        />
                    )}
                    <SmsTenantTable
                        rows={data?.items ?? []}
                        loading={isLoading}
                        showVoice={!!features?.voice}
                        onOpen={(id) => push(`/sms-messaging/tenants/${id}`)}
                    />
                    <Space style={{ marginTop: 16 }}>
                        <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                            {t('actions.previous')}
                        </Button>
                        <span>{t('list.page', { page })}</span>
                        <span>{t('list.total', { total: data?.total ?? 0 })}</span>
                        <Button
                            disabled={!data || page * data.pageSize >= data.total}
                            onClick={() => setPage(page + 1)}
                        >
                            {t('actions.next')}
                        </Button>
                    </Space>
                </section>
            )}
        </SaSmsGuard>
    );
};
